import React from "react";
import { formatDistanceToNowStrict } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import { Waves, AlertTriangle } from "lucide-react";

const depthColor = (d) => (d < 50 ? "bg-red-500" : d <= 300 ? "bg-orange-500" : "bg-emerald-500");
const depthText = (d) => (d < 50 ? "text-red-400" : d <= 300 ? "text-orange-400" : "text-emerald-400");

export default function QuakeFeed({ quakes, onSelect, onGenerateCard, activeId }) {
  return (
    <div className="flex h-full flex-col" data-testid="quake-feed">
      <div className="border-b border-slate-800 px-4 py-2.5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Live Feed</div>
            <div className="font-mono text-sm text-slate-200">{quakes.length} events</div>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-400">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75"></span>
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
            </span>
            Streaming
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {quakes.length === 0 && (
          <div className="p-8 text-center text-xs text-slate-500">Tidak ada gempa sesuai filter.</div>
        )}
        {quakes.map((q) => {
          const active = activeId === q.id;
          return (
            <div
              key={q.id}
              onClick={() => onSelect(q)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(q); } }}
              role="button"
              tabIndex={0}
              className={`group flex w-full cursor-pointer items-start gap-3 border-b border-slate-800/60 px-4 py-3 text-left hover:bg-slate-900/60 ${active ? "bg-slate-900/80" : ""}`}
              data-testid={`quake-item-${q.id}`}
            >
              <div className="flex flex-col items-center gap-1 pt-0.5">
                <div className={`h-8 w-8 shrink-0 rounded-full ${depthColor(q.depth)} flex items-center justify-center font-mono text-[11px] font-bold text-white ring-2 ring-slate-950`}>
                  {q.magnitude?.toFixed(1)}
                </div>
                {q.tsunami && <Waves className="h-3 w-3 text-cyan-400" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-slate-100">{q.location}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-400">
                  <span className={`font-mono ${depthText(q.depth)}`}>{q.depth?.toFixed(1)} km</span>
                  <span className="font-mono">{q.latitude?.toFixed(2)}, {q.longitude?.toFixed(2)}</span>
                  <span>{formatDistanceToNowStrict(new Date(q.time), { addSuffix: true, locale: idLocale })}</span>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <span className={`rounded-md border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${q.source === "BMKG" ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-300" : "border-violet-500/40 bg-violet-500/10 text-violet-300"}`}>{q.source}</span>
                  {q.tsunami && (
                    <span className="flex items-center gap-1 rounded-md border border-red-500/40 bg-red-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-red-300">
                      <AlertTriangle className="h-2.5 w-2.5" /> Tsunami
                    </span>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); onGenerateCard(q); }}
                    className="ml-auto text-[10px] font-semibold text-cyan-400 opacity-0 hover:underline group-hover:opacity-100"
                    data-testid={`quake-share-${q.id}`}
                  >
                    Bagikan
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
